package com.schoolconnect.app.feature.exams

import com.schoolconnect.app.datasource.network.UserSessionStore
import com.schoolconnect.app.domain.SchoolKeys
import com.schoolconnect.app.datasource.network.request.ExamCreateRequest
import com.schoolconnect.app.datasource.network.request.ExamUpdateRequest
import com.schoolconnect.app.datasource.network.request.SubjectMarkConfigRequest
import com.schoolconnect.app.datasource.network.request.ExamResultBulkEntryItemRequest
import com.schoolconnect.app.datasource.network.request.ExamResultBulkEntryRequest
import com.schoolconnect.app.datasource.network.request.ExamResultSubjectMarkRequest
import com.schoolconnect.app.datasource.network.request.ExamResultSummaryRequest
import com.schoolconnect.app.datasource.network.response.ExamResultSheetResponse
import com.schoolconnect.app.datasource.network.response.StudentExamResultResponse
import com.schoolconnect.app.designsystem.AppToastCenter
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.repository.exam.ExamRepository
import com.schoolconnect.app.repository.classsection.ClassSectionRepository
import com.schoolconnect.app.repository.classsubjectlink.ClassSubjectLinkRepository
import com.schoolconnect.app.repository.schoolstaff.SchoolStaffRepository
import com.schoolconnect.app.repository.subject.SubjectRepository
import com.schoolconnect.app.repository.systemlookup.SystemLookupOptions
import com.schoolconnect.app.repository.systemlookup.SystemLookupRepository
import com.schoolconnect.app.repository.examresult.ExamResultRepository
import com.schoolconnect.app.runtime.AppErrorCenter
import com.schoolconnect.app.util.scLog
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.async
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.serialization.json.jsonPrimitive

private fun examTypeServerCode(value: String): String {
    return value.trim()
}

private fun examStatusServerCode(value: String): String =
    SystemLookupOptions.code("EXAM_STATUS", value).ifBlank {
        SystemLookupOptions.codes("EXAM_STATUS").firstOrNull()
            ?: error("No active values are available in lookup category 'EXAM_STATUS'.")
    }

class ExamsViewModel(
    private val schoolId: String = UserSessionStore.getHomeRoleSchoolId(),
    private val currentUserId: String = UserSessionStore.getUserId()
) {
    private val isTeacher = UserSessionStore.getHomeRoleCode() == SchoolKeys.V_ROLE_TEACHER
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private var loadJob: Job? = null
    private var statisticsJob: Job? = null
    private var lookupJob: Job? = null
    private var subjectLookupJob: Job? = null
    private var saveJob: Job? = null
    private var examDetailsJob: Job? = null
    private var viewResultsJob: Job? = null
    private var editResultsJob: Job? = null
    private var insightsJob: Job? = null
    private var importExportJob: Job? = null
    private val subjectOptionsByClassSectionId = mutableMapOf<String, Map<String, String>>()
    private val _uiState = MutableStateFlow(
        ExamsUiState(
            isLoading = true,
            // Lookup loading starts with the ViewModel. This prevents the Add
            // Exam dialog from showing a false empty state before the first
            // class-specific subject request has started.
            isLoadingSubjects = true
        )
    )
    val uiState: StateFlow<ExamsUiState> = _uiState.asStateFlow()

    init { loadPage(); loadStatistics(); loadLookups() }

    fun onEvent(event: ExamsUiEvent) {
        when (event) {
            ExamsUiEvent.AddRequested -> _uiState.update { it.copy(saveErrorMessage = "", editExamError = "") }
            is ExamsUiEvent.ClassSelected -> loadSubjectsForClass(event.classLabel)
            is ExamsUiEvent.SearchChanged -> { _uiState.update { it.copy(searchQuery = event.query, pageIndex = 0) }; loadPage(debounce = true) }
            is ExamsUiEvent.FiltersChanged -> { _uiState.update { it.copy(filters = event.filters, pageIndex = 0) }; loadPage() }
            ExamsUiEvent.FiltersApplied -> loadPage()
            ExamsUiEvent.FiltersReset -> { _uiState.update { it.copy(searchQuery = "", filters = ExamsFilters(), pageIndex = 0) }; loadPage() }
            ExamsUiEvent.RefreshClicked -> { loadPage(forceRemote = true); loadStatistics() }
            is ExamsUiEvent.PageChanged -> { _uiState.update { it.copy(pageIndex = event.pageIndex.coerceAtLeast(0)) }; loadPage() }
            is ExamsUiEvent.PageSizeChanged -> { _uiState.update { it.copy(pageSize = event.pageSize.coerceIn(PaginationPolicy.PAGE_SIZE_OPTIONS.min(), PaginationPolicy.PAGE_SIZE_OPTIONS.max()), pageIndex = 0) }; loadPage() }
            is ExamsUiEvent.SortChanged -> {
                _uiState.update {
                    val ascending = if (it.sortField == event.field) !it.sortAscending else true
                    it.copy(sortField = event.field, sortAscending = ascending, pageIndex = 0)
                }
                loadPage()
            }
            is ExamsUiEvent.DeleteClicked -> deleteExam(event.id)
            is ExamsUiEvent.AddExam -> _uiState.update { state ->
                val nextId = "EX-${(state.rows.size + 1).toString().padStart(4, '0')}"
                state.copy(rows = listOf(ExamRowUi(nextId, event.name, event.type, event.classes, event.date, "Draft")) + state.rows)
            }
            is ExamsUiEvent.CreateExam -> createExam(event.draft)
            is ExamsUiEvent.UpdateExam -> updateExam(event)
            is ExamsUiEvent.ViewResultsClicked -> if (event.exam.isPublished()) loadViewResults(event.exam)
            is ExamsUiEvent.ViewExamDetailsClicked -> loadExamDetails(event.exam)
            ExamsUiEvent.CloseExamDetails -> { examDetailsJob?.cancel(); _uiState.update { it.copy(selectedExamDetails = null, isLoadingExamDetails = false, examDetailsError = "") } }
            is ExamsUiEvent.EditExamClicked -> loadEditExam(event.exam)
            ExamsUiEvent.CloseEditExam -> { examDetailsJob?.cancel(); _uiState.update { it.copy(selectedEditExam = null, isLoadingEditExam = false, editExamError = "", saveErrorMessage = "") } }
            is ExamsUiEvent.EditResultsClicked -> loadEditResults(event.exam)
            is ExamsUiEvent.DownloadImportTemplateClicked -> downloadImportTemplate(event.examId)
            is ExamsUiEvent.DownloadResultsExcelClicked -> downloadResultsExcel(event.examId)
            is ExamsUiEvent.ImportExamResultsClicked -> importExamResults(event.examId, event.fileName, event.bytes)
            is ExamsUiEvent.SaveExamResultsClicked -> saveExamResults(event)
            ExamsUiEvent.CloseEditResults -> { editResultsJob?.cancel(); _uiState.update { it.copy(selectedEditResultsExam = null, isLoadingEditResults = false, editResultsError = "", editResultsSubjectNameError = "") } }
            ExamsUiEvent.CloseResults -> { viewResultsJob?.cancel(); _uiState.update { it.copy(selectedResultsExam = null, results = emptyList(), isLoadingResults = false, resultsError = "") } }
            is ExamsUiEvent.ViewInsightsClicked -> if (event.exam.isPublished()) loadInsights(event.exam)
            ExamsUiEvent.CloseInsights -> { insightsJob?.cancel(); _uiState.update { it.copy(selectedInsightsExam = null, insights = null, isLoadingInsights = false, insightsError = "") } }
        }
    }

    private fun ExamRowUi.isPublished(): Boolean = status.equals("Published", ignoreCase = true)

    private fun loadInsights(row: ExamRowUi) {
        insightsJob?.cancel()
        _uiState.update { it.copy(selectedInsightsExam = row, insights = null, isLoadingInsights = true, insightsError = "") }
        insightsJob = scope.launch {
            ExamResultRepository.loadExamInsights(row.backendId.trim()).fold(
                onSuccess = { response ->
                    val classLabel = listOfNotNull(response.className?.trim(), response.sectionName?.trim())
                        .filter { it.isNotBlank() }
                        .joinToString("-")
                        .ifBlank { row.classes }
                    val status = if (response.published == true) {
                        "Published"
                    } else {
                        response.status?.trim().orEmpty().ifBlank { row.status }
                    }
                    val mapped = ExamInsightsUi(
                        totalStudents = response.totalStudents ?: 0,
                        averagePercentage = response.overallPercentageAverage?.let { "${it.toDisplayNumber()}%" }.orEmpty(),
                        status = status,
                        classLabel = classLabel,
                        buckets = response.performanceBuckets.orEmpty().map { bucket ->
                            ExamInsightBucketUi(
                                label = bucket.displayValue.orEmpty().ifBlank { bucket.bucket.orEmpty() },
                                count = bucket.count ?: 0,
                                percentage = bucket.percentage?.let { "${it.toDisplayNumber()}%" }.orEmpty()
                            )
                        },
                        topStudents = response.top5Students.orEmpty().map { student ->
                            ExamInsightStudentUi(
                                rank = student.rank?.toString().orEmpty(),
                                rollNumber = student.rollNumber.orEmpty(),
                                name = student.studentName.orEmpty().ifBlank { "Unnamed student" },
                                totalMarks = student.totalMarks?.toDisplayNumber().orEmpty(),
                                percentage = student.percentage?.let { "${it.toDisplayNumber()}%" }.orEmpty()
                            )
                        },
                        bottomStudents = response.bottom5Students.orEmpty().map { student ->
                            ExamInsightStudentUi(
                                rank = student.rank?.toString().orEmpty(),
                                rollNumber = student.rollNumber.orEmpty(),
                                name = student.studentName.orEmpty().ifBlank { "Unnamed student" },
                                totalMarks = student.totalMarks?.toDisplayNumber().orEmpty(),
                                percentage = student.percentage?.let { "${it.toDisplayNumber()}%" }.orEmpty()
                            )
                        }
                    )
                    _uiState.update { it.copy(insights = mapped, isLoadingInsights = false, insightsError = "") }
                },
                onFailure = { throwable ->
                    scLog("[DATA_RETRIEVE_SC] ExamsViewModel loadInsights failure examId=${row.backendId}", throwable)
                    _uiState.update { it.copy(insights = null, isLoadingInsights = false, insightsError = throwable.message ?: "Unable to load exam insights.") }
                }
            )
        }
    }

    private fun loadViewResults(row: ExamRowUi) {
        viewResultsJob?.cancel()
        _uiState.update { it.copy(selectedResultsExam = row, results = emptyList(), isLoadingResults = true, resultsError = "") }
        viewResultsJob = scope.launch {
            val examId = row.backendId.trim()
            val overall = ExamResultRepository.loadExamResults(examId)
            val subjectSummary = ExamResultRepository.loadExamResultsSummary(examId)
            val subjectsByStudent = subjectSummary.getOrNull()?.results.orEmpty()
                .groupBy { it.studentId.orEmpty().trim() }
            val subjectNames = _uiState.value.subjectOptionIds.entries.associate { (name, id) -> id to name }
            val mapped = overall.getOrNull()?.items.orEmpty().mapIndexed { index, item ->
                val studentId = item.studentId?.trim().orEmpty()
                val subjectRows = subjectsByStudent[studentId].orEmpty()
                ExamResultPreviewUi(
                    rollNumber = item.rollNumber?.trim().orEmpty().ifBlank { (index + 1).toString().padStart(2, '0') },
                    studentName = item.studentName?.trim().orEmpty().ifBlank { "Unnamed student" },
                    subject = subjectRows.firstOrNull()?.subjectId.orEmpty(),
                    marks = item.totalMarksObtained?.takeIf { it > 0.0 }?.toDisplayNumber().orEmpty(),
                    totalMarks = item.totalMaxMarks?.takeIf { it > 0.0 }?.toDisplayNumber().orEmpty(),
                    percentage = item.percentage?.let { "${it.toDisplayNumber()}%" }.orEmpty(),
                    grade = item.overallGrade.orEmpty(),
                    rank = item.overallRank?.takeIf { it > 0 }?.toString().orEmpty(),
                    notes = item.notes.orEmpty(),
                    subjects = subjectRows.map { subject ->
                        val absent = subject.isAbsent == true || subject.grade.orEmpty().equals("AB", ignoreCase = true)
                        val max = row.subjectConfigs[subject.subjectId.orEmpty()]?.totalMark?.toDoubleOrNull()
                        val marks = if (absent) "AB" else subject.marksObtained?.takeIf { it > 0.0 }?.toDisplayNumber().orEmpty()
                        val total = if (absent) "AB" else max?.toDisplayNumber().orEmpty()
                        val percentage = if (absent) "AB" else if (max != null && max > 0.0 && subject.marksObtained != null) {
                            "${(kotlin.math.round(subject.marksObtained / max * 10000.0) / 100.0).toDisplayNumber()}%"
                        } else ""
                        ExamSubjectPreviewUi(
                            name = subjectNames[subject.subjectId.orEmpty()].orEmpty().ifBlank { subject.subjectId.orEmpty() },
                            marks = marks,
                            totalMarks = total,
                            percentage = percentage
                        )
                    }
                )
            }
            val error = overall.exceptionOrNull()?.message ?: subjectSummary.exceptionOrNull()?.message.orEmpty()
            _uiState.update { it.copy(results = mapped, isLoadingResults = false, resultsError = error) }
        }
    }

    private fun loadEditResults(row: ExamRowUi) {
        editResultsJob?.cancel()
        _uiState.update { it.copy(selectedEditResultsExam = row, isLoadingEditResults = true, editResultsError = "", editResultsSubjectNameError = "") }
        editResultsJob = scope.launch {
            val examId = row.backendId.trim()
            val subjectOptionsDeferred = async { SubjectRepository.loadSchoolSubjectOptions(schoolId) }
            val results = ExamResultRepository.loadExamResults(examId)
            val summary = ExamResultRepository.loadExamResultsSummary(examId)
            val subjectOptionsResult = subjectOptionsDeferred.await()
            val subjectNamesById = subjectOptionsResult.getOrNull()?.nameById.orEmpty()
            val subjectCodesById = subjectOptionsResult.getOrNull()?.codeById.orEmpty()
            val existingSubjectNamesById = _uiState.value.subjectOptionIds.entries.associate { (name, id) -> id to name }
            val unresolvedSubjectCount = row.subjectConfigs.keys.count { subjectId ->
                subjectId !in subjectNamesById && subjectId !in existingSubjectNamesById
            }
            val subjectNameError = subjectOptionsResult.exceptionOrNull()?.let { throwable ->
                "Unable to load subject names: ${throwable.message ?: "Please retry."}"
            } ?: unresolvedSubjectCount.takeIf { it > 0 }?.let { count ->
                "Unable to resolve names for $count configured subject${if (count == 1) "" else "s"}. Verify the exam's subject configuration."
            } ?: row.subjectConfigs.keys.count { subjectCodesById[it].isNullOrBlank() }
                .takeIf { it > 0 }
                ?.let { count -> "Unable to resolve codes for $count configured subject${if (count == 1) "" else "s"}. Add short codes in subject settings." }
                .orEmpty()
            val subjectResultsByStudent = summary.getOrNull()?.results.orEmpty()
                .groupBy { it.studentId.orEmpty().trim() }
            val error = results.exceptionOrNull()?.message ?: summary.exceptionOrNull()?.message.orEmpty()
            val students = results.getOrNull()?.items.orEmpty().mapIndexed { index, item ->
                val key = item.studentId?.trim().takeIf { !it.isNullOrBlank() }
                    ?: item.rollNumber?.trim().takeIf { !it.isNullOrBlank() }
                    ?: "student-$index"
                ExamResultStudentUi(
                    key = key,
                    studentId = item.studentId?.trim().orEmpty(),
                    roll = item.rollNumber?.trim().orEmpty(),
                    name = item.studentName?.trim().orEmpty().ifBlank { "Unnamed student" },
                    grade = item.overallGrade?.trim().orEmpty(),
                    percent = item.percentage?.let { "${it}%" }.orEmpty(),
                    total = item.totalMarksObtained?.takeIf { it > 0.0 }?.toDisplayNumber().orEmpty(),
                    rank = item.overallRank
                        ?.takeIf { it > 0 && item.hasRecordedResult(subjectResultsByStudent[key].orEmpty()) }
                        ?.toString()
                        .orEmpty(),
                    notes = item.notes?.trim().orEmpty(),
                    subjectValues = subjectResultsByStudent[item.studentId?.trim().orEmpty()]
                        .orEmpty()
                        .mapNotNull { subject ->
                            val subjectId = subject.subjectId?.trim().orEmpty()
                            if (subjectId.isBlank()) return@mapNotNull null
                            // The bulk-entry API may persist the AB marker in
                            // `grade` without setting the derived `is_absent`
                            // flag. Treat both representations as absent when
                            // rebuilding the input row.
                            val absent = subject.isAbsent == true || subject.grade.orEmpty().equals("AB", ignoreCase = true)
                            val hasRecordedSubjectData = absent ||
                                (subject.marksObtained?.takeIf { it > 0.0 } != null) ||
                                subject.grade.orEmpty().isNotBlank()
                            subjectId to listOf(
                                if (absent) "AB" else subject.grade.orEmpty(),
                                if (absent) "AB" else subject.marksObtained?.takeIf { it > 0.0 }?.toDisplayNumber().orEmpty(),
                                if (absent) "AB" else subject.rank?.takeIf { it > 0 && hasRecordedSubjectData }?.toString().orEmpty()
                            ).joinToString(" / ")
                        }.toMap()
                )
            }.sortedWith(
                compareBy<ExamResultStudentUi> { it.roll.toIntOrNull() ?: Int.MAX_VALUE }
                    .thenBy { it.roll }
                    .thenBy { it.name }
            )
            _uiState.update {
                it.copy(
                    isLoadingEditResults = false,
                    editResultsError = error,
                    editResultsSubjectNameError = subjectNameError,
                    subjectNamesById = subjectNamesById,
                    subjectCodesById = subjectCodesById,
                    editResultsStudentCount = results.getOrNull()?.count ?: students.size,
                    editResultsStudents = students
                )
            }
        }
    }

    private fun StudentExamResultResponse.hasRecordedResult(
        subjectResults: List<ExamResultSheetResponse>
    ): Boolean =
        totalMarksObtained?.takeIf { it > 0.0 } != null ||
            overallGrade.orEmpty().isNotBlank() ||
            notes.orEmpty().isNotBlank() ||
            subjectResults.any {
                it.isAbsent == true ||
                    it.marksObtained?.takeIf { marks -> marks > 0.0 } != null ||
                    it.grade.orEmpty().isNotBlank()
            }

    private fun saveExamResults(event: ExamsUiEvent.SaveExamResultsClicked) {
        if (_uiState.value.isSaving) return
        val examId = event.examId.trim()
        val classSectionId = event.classSectionId.trim()
        val results = event.results
        val error = when {
            examId.isBlank() -> "Unable to identify the exam."
            classSectionId.isBlank() -> "Unable to identify the class section."
            results.isEmpty() -> "Enter at least one student result."
            results.any { it.studentId.isBlank() } -> "A student identifier is missing. Reload the results and try again."
            else -> null
        }
        if (error != null) {
            _uiState.update { it.copy(saveErrorMessage = error) }
            return
        }
        saveJob?.cancel()
        saveJob = scope.launch {
            _uiState.update { it.copy(isSaving = true, saveErrorMessage = "") }
            val request = ExamResultBulkEntryRequest(
                examId = examId,
                status = event.status,
                results = results.map { result ->
                    ExamResultBulkEntryItemRequest(
                        studentId = result.studentId,
                        rollNumber = result.rollNumber,
                        name = result.name,
                        summary = ExamResultSummaryRequest(
                            total = result.total,
                            totalPercentage = result.totalPercentage,
                            grade = result.grade,
                            rank = result.rank,
                            notes = result.notes
                        ),
                        subjectMarks = result.subjectMarks.map { subject ->
                            ExamResultSubjectMarkRequest(
                                subjectId = subject.subjectId,
                                internal = subject.internal,
                                main = subject.main,
                                total = subject.total,
                                grade = subject.grade,
                                rank = subject.rank
                            )
                        }
                    )
                }
            )
            val saveResult = ExamResultRepository.saveExamResults(classSectionId, request)
            saveResult.exceptionOrNull()?.let { throwable ->
                scLog("[DATA_RETRIEVE_SC] ExamsViewModel saveExamResults failure examId=$examId", throwable)
                _uiState.update { it.copy(isSaving = false, saveErrorMessage = throwable.message ?: "Unable to save exam results.") }
                return@launch
            }

            if (event.status.equals("PUBLISHED", ignoreCase = true)) {
                val publishResult = ExamResultRepository.publishExamResults(examId)
                publishResult.exceptionOrNull()?.let { throwable ->
                    scLog("[DATA_RETRIEVE_SC] ExamsViewModel publishExamResults failure examId=$examId", throwable)
                    _uiState.update { it.copy(isSaving = false, saveErrorMessage = throwable.message ?: "Results were saved, but publishing failed.") }
                    return@launch
                }
            }

            val published = event.status.equals("PUBLISHED", ignoreCase = true)
            AppToastCenter.showSuccess(
                if (published) "Exam results published successfully." else "Exam results saved as draft."
            )
            _uiState.update {
                it.copy(
                    isSaving = false,
                    saveErrorMessage = "",
                    selectedEditResultsExam = null,
                    isLoadingEditResults = false,
                    editResultsError = "",
                    editResultsSubjectNameError = ""
                )
            }
            loadPage(forceRemote = true)
        }
    }

    private fun downloadImportTemplate(examId: String) {
        importExportJob?.cancel()
        importExportJob = scope.launch {
            _uiState.update { it.copy(isLoadingImportExport = true, importExportError = "") }
            ExamResultRepository.downloadImportTemplate(examId).fold(
                onSuccess = { _uiState.update { it.copy(isLoadingImportExport = false) } },
                onFailure = { throwable -> _uiState.update { it.copy(isLoadingImportExport = false, importExportError = throwable.message ?: "Unable to download the exam template.") } }
            )
        }
    }

    private fun downloadResultsExcel(examId: String) {
        importExportJob?.cancel()
        importExportJob = scope.launch {
            _uiState.update { it.copy(isLoadingImportExport = true, importExportError = "") }
            ExamResultRepository.downloadResultsExport(examId).fold(
                onSuccess = { _uiState.update { it.copy(isLoadingImportExport = false) } },
                onFailure = { throwable -> _uiState.update { it.copy(isLoadingImportExport = false, importExportError = throwable.message ?: "Unable to download exam results.") } }
            )
        }
    }

    private fun importExamResults(examId: String, fileName: String, bytes: ByteArray) {
        importExportJob?.cancel()
        importExportJob = scope.launch {
            _uiState.update { it.copy(isLoadingImportExport = true, importExportError = "") }
            ExamResultRepository.importExamResults(examId, fileName, bytes).fold(
                onSuccess = { response ->
                    val importErrors = response.validationErrors.orEmpty()
                    if (importErrors.isNotEmpty()) {
                        _uiState.update { it.copy(isLoadingImportExport = false, importExportError = importErrors.joinToString("; ")) }
                    } else {
                        _uiState.update { it.copy(isLoadingImportExport = false) }
                        _uiState.value.selectedEditResultsExam?.let { loadEditResults(it) }
                    }
                },
                onFailure = { throwable -> _uiState.update { it.copy(isLoadingImportExport = false, importExportError = throwable.message ?: "Unable to import exam results.") } }
            )
        }
    }

    private fun loadLookups() {
        lookupJob?.cancel()
        lookupJob = scope.launch {
            try {
            val lookupSnapshot = SystemLookupRepository.refresh(forceRemote = false).getOrElse { throw it }
            val typeItems = lookupSnapshot.activeValues("EXAM_TYPE").ifEmpty {
                SystemLookupRepository.loadCategoryItems("EXAM_TYPE").getOrElse { throw it }
            }
            val statusItems = lookupSnapshot.activeValues("EXAM_STATUS").ifEmpty {
                SystemLookupRepository.loadCategoryItems("EXAM_STATUS").getOrElse { throw it }
            }
            val typeOptions = typeItems.mapNotNull { it.displayValue?.trim()?.takeIf(String::isNotBlank) ?: it.code?.trim()?.takeIf(String::isNotBlank) }
            val statusOptions = statusItems.mapNotNull { it.displayValue?.trim()?.takeIf(String::isNotBlank) ?: it.code?.trim()?.takeIf(String::isNotBlank) }
            check(typeOptions.isNotEmpty()) { "No active exam types are configured. Ask an administrator to configure EXAM_TYPE values." }
            check(statusOptions.isNotEmpty()) { "No active exam statuses are configured. Ask an administrator to configure EXAM_STATUS values." }

            val classIds = if (isTeacher) {
                SchoolStaffRepository.loadCurrentTeacherWithAssignments().getOrElse { throw it }
                    .teachingAssignments.orEmpty()
                    .filter { it.isActive != false }
                    .mapNotNull { assignment ->
                        val id = assignment.classSectionId?.trim().orEmpty()
                        val label = listOfNotNull(assignment.className?.trim(), assignment.sectionName?.trim())
                            .filter(String::isNotBlank)
                            .joinToString("-")
                        if (id.isBlank() || label.isBlank()) null else label to id
                    }.distinctBy { it.second }.toMap()
            } else {
                ClassSectionRepository.loadSchoolClassSectionLookup(schoolId).getOrElse { throw it }
                    .mapNotNull { item ->
                        val id = item.id?.trim().orEmpty()
                        val label = listOfNotNull(item.className?.trim(), item.sectionName?.trim())
                            .filter(String::isNotBlank)
                            .joinToString("-")
                        if (id.isBlank() || label.isBlank()) null else label to id
                    }.toMap()
            }
            check(classIds.isNotEmpty()) { "No active classes are available for the current academic year." }
            _uiState.update {
                it.copy(
                    classOptionIds = classIds,
                    examTypeOptions = typeOptions,
                    examStatusOptions = statusOptions,
                    subjectOptionIds = emptyMap(),
                    isLoadingSubjects = classIds.isNotEmpty()
                )
            }
            classIds.keys.firstOrNull()?.let { loadSubjectsForClass(it) }
            } catch (throwable: Throwable) {
                if (throwable is CancellationException) throw throwable
                _uiState.update { it.copy(saveErrorMessage = "Unable to load exam options: ${throwable.message ?: "Please refresh and try again."}", isLoadingSubjects = false) }
            }
        }
    }

    private fun loadSubjectsForClass(classLabel: String) {
        val classSectionId = _uiState.value.classOptionIds[classLabel].orEmpty().trim()
        subjectLookupJob?.cancel()
        if (classSectionId.isBlank()) {
            _uiState.update { it.copy(subjectOptionIds = emptyMap(), isLoadingSubjects = false) }
            return
        }
        subjectLookupJob = scope.launch {
            _uiState.update { it.copy(subjectOptionIds = emptyMap(), isLoadingSubjects = true) }
            subjectOptionsByClassSectionId[classSectionId]?.let { cachedOptions ->
                _uiState.update {
                    it.copy(
                        subjectOptionIds = cachedOptions,
                        isLoadingSubjects = false
                    )
                }
                return@launch
            }
            val linkedSubjectIds = ClassSubjectLinkRepository.loadClassSectionSubjectLinks(
                schoolId = schoolId,
                classSectionId = classSectionId,
                forceRemote = true
            ).getOrElse { throwable ->
                _uiState.update { it.copy(isLoadingSubjects = false, saveErrorMessage = "Unable to load subjects for the selected class: ${throwable.message ?: "Please retry."}") }
                return@launch
            }.mapNotNull { it.subjectId?.trim()?.takeIf(String::isNotBlank) }.toSet()
            val subjectsResult = SubjectRepository.loadSchoolSubjectsPage(
                schoolId = schoolId,
                isActive = true,
                pageIndex = 0,
                pageSize = 100
            ).getOrElse { throwable ->
                _uiState.update { it.copy(isLoadingSubjects = false, saveErrorMessage = "Unable to load available subjects: ${throwable.message ?: "Please retry."}") }
                return@launch
            }
            val options = subjectsResult.mapNotNull { subject ->
                val id = subject.id?.trim().orEmpty()
                val name = subject.name?.trim().orEmpty()
                if (id.isBlank() || name.isBlank() || id !in linkedSubjectIds) null else name to id
            }.toMap()
            if (options.isEmpty()) _uiState.update { it.copy(saveErrorMessage = "No active subjects are linked to the selected class. Update class subject links before creating an exam.") }
            subjectOptionsByClassSectionId[classSectionId] = options
            _uiState.update { it.copy(subjectOptionIds = options, isLoadingSubjects = false) }
        }
    }

    private fun createExam(draft: ExamDraft) {
        if (_uiState.value.isSaving) return
        val classIds = draft.classSectionIds.map(String::trim).filter(String::isNotBlank).distinct()
        val subjects = draft.subjectMarkConfigs.filter { it.subjectId.isNotBlank() }
        val startDate = parseDisplayDate(draft.startDate)
        val endDate = parseDisplayDate(draft.endDate)
        val marksSubjects = subjects.filter { it.hasMarks }
        val maxMarks = marksSubjects.sumOf { it.totalMark.toDoubleOrNull() ?: 0.0 }
        val requiresMaximumMarks = draft.hasOverallMarks && marksSubjects.isNotEmpty()
        val error = when {
            classIds.isEmpty() -> "Select at least one class."
            subjects.isEmpty() -> "Configure at least one subject with a valid subject selection."
            requiresMaximumMarks && maxMarks <= 0.0 -> "Enter maximum marks for the selected subjects."
            draft.title.trim().isBlank() -> "Enter an exam name."
            startDate == null || endDate == null -> "Enter valid dates in DD MMM YYYY format."
            else -> null
        }
        if (error != null) {
            _uiState.update { it.copy(saveErrorMessage = error) }
            return
        }

        saveJob?.cancel()
        saveJob = scope.launch {
            _uiState.update { it.copy(isSaving = true, saveErrorMessage = "") }
            ExamRepository.create(
                request = ExamCreateRequest(
                    classSectionId = classIds.firstOrNull(),
                    subjectId = subjects.firstOrNull()?.subjectId,
                    title = draft.title.trim(),
                    examType = examTypeServerCode(draft.examType),
                    maxMarks = maxMarks,
                    // These are calendar dates in the school's configured timezone.
                    // Send them as naive local datetimes so the backend's AppDateTime
                    // binds them to the application timezone instead of shifting the
                    // inclusive end date into the following calendar day.
                    examDate = "${startDate}T00:00:00",
                    startDate = "${startDate}T00:00:00",
                    endDate = "${endDate}T23:59:59",
                    status = examStatusServerCode(draft.status),
                    examId = draft.examId.trim().takeIf { it.isNotBlank() },
                    hasOverallGrade = draft.hasOverallGrade,
                    hasOverallRank = draft.hasOverallRank,
                    hasOverallMarks = draft.hasOverallMarks,
                    // The backend requires this list to contain at least one
                    // applicable class, including for a single-class exam.
                    applicableClassSectionIds = classIds,
                    subjectMarkConfigs = subjects.map { config ->
                        com.schoolconnect.app.datasource.network.request.SubjectMarkConfigRequest(
                            subjectId = config.subjectId,
                            internalMark = config.internalMark.toDoubleOrNull() ?: 0.0,
                            mainMark = config.mainMark.toDoubleOrNull() ?: 0.0,
                            totalMark = config.totalMark.toDoubleOrNull() ?: 0.0,
                            hasGrade = config.hasGrade,
                            hasRank = config.hasRank,
                            hasMarks = config.hasMarks
                        )
                    }
                )
            ).fold(
                onSuccess = {
                    _uiState.update { it.copy(isSaving = false, saveErrorMessage = "", saveGeneration = it.saveGeneration + 1) }
                    loadPage(forceRemote = true)
                    loadStatistics()
                },
                onFailure = { throwable ->
                    _uiState.update { it.copy(isSaving = false, saveErrorMessage = throwable.message ?: "Unable to create exam.") }
                }
            )
        }
    }

    private fun deleteExam(examId: String) {
        if (_uiState.value.isSaving) return
        saveJob?.cancel()
        saveJob = scope.launch {
            _uiState.update { it.copy(isSaving = true, errorMessage = "") }
            ExamRepository.delete(examId).fold(
                onSuccess = {
                    _uiState.update { it.copy(isSaving = false, pageIndex = 0) }
                    AppToastCenter.showSuccess("Exam deleted.")
                    loadPage(forceRemote = true)
                    loadStatistics()
                },
                onFailure = { throwable ->
                    _uiState.update { it.copy(isSaving = false) }
                    AppErrorCenter.emit(
                        throwable,
                        source = "ExamsViewModel",
                        fallbackMessage = "Unable to delete exam."
                    )
                }
            )
        }
    }

    private fun updateExam(event: ExamsUiEvent.UpdateExam) {
        if (_uiState.value.isSaving) return
        val startDate = parseDisplayDate(event.startDate)
        val endDate = parseDisplayDate(event.endDate)
        val error = when {
            event.id.isBlank() -> "Unable to identify the exam."
            event.name.trim().isBlank() -> "Enter an exam name."
            startDate == null || endDate == null -> "Enter valid dates in DD MMM YYYY format."
            else -> null
        }
        if (error != null) {
            _uiState.update { it.copy(saveErrorMessage = error) }
            return
        }

        saveJob?.cancel()
        saveJob = scope.launch {
            _uiState.update { it.copy(isSaving = true, saveErrorMessage = "") }
            val subjectConfigs = event.subjectMarkConfigs.mapNotNull { config ->
                val totalMark = config.totalMark.toDoubleOrNull() ?: return@mapNotNull null
                SubjectMarkConfigRequest(
                    subjectId = config.subjectId,
                    internalMark = config.internalMark.toDoubleOrNull() ?: 0.0,
                    mainMark = config.mainMark.toDoubleOrNull() ?: 0.0,
                    totalMark = totalMark,
                    hasGrade = config.hasGrade,
                    hasRank = config.hasRank,
                    hasMarks = config.hasMarks
                )
            }
            ExamRepository.update(
                examId = event.id,
                request = ExamUpdateRequest(
                    title = event.name.trim(),
                    examType = examTypeServerCode(event.type),
                    startDate = "${startDate}T00:00:00",
                    endDate = "${endDate}T23:59:59",
                    status = examStatusServerCode(event.status),
                    maxMarks = event.maxMarks ?: subjectConfigs.filter { it.hasMarks }.sumOf { it.totalMark }.takeIf { it > 0.0 },
                    hasOverallGrade = event.hasOverallGrade,
                    hasOverallRank = event.hasOverallRank,
                    hasOverallMarks = event.hasOverallMarks,
                    subjectMarkConfigs = subjectConfigs.takeIf { it.isNotEmpty() }
                )
            ).fold(
                onSuccess = {
                    _uiState.update { it.copy(isSaving = false, saveErrorMessage = "", saveGeneration = it.saveGeneration + 1) }
                    loadPage(forceRemote = true)
                    loadStatistics()
                },
                onFailure = { throwable ->
                    scLog("[DATA_RETRIEVE_SC] ExamsViewModel updateExam failure examId=${event.id}", throwable)
                    _uiState.update { it.copy(isSaving = false, saveErrorMessage = throwable.message ?: "Unable to update exam.") }
                }
            )
        }
    }

    private fun loadPage(forceRemote: Boolean = false, debounce: Boolean = false) {
        loadJob?.cancel()
        loadJob = scope.launch {
            if (debounce) delay(300)
            val state = _uiState.value
            val apiStatus = state.filters.status.takeIf { it.isNotBlank() }
                ?.let { SystemLookupOptions.serverCode("EXAM_STATUS", it) }
            val classSectionId = state.filters.classSectionId.trim().takeIf(String::isNotBlank)
            _uiState.update { it.copy(isLoading = true, errorMessage = "") }
            val resultProgressByExamId = ExamResultRepository.loadTeacherPage(
                search = state.searchQuery,
                status = apiStatus,
                classSectionId = classSectionId,
                pageIndex = state.pageIndex,
                pageSize = state.pageSize,
                sortBy = state.sortField.toApiSortField(),
                order = if (state.sortAscending) "asc" else "desc",
                refreshScope = scope
            ).getOrNull()?.items.orEmpty().associateBy { item ->
                item.examId.orEmpty().ifBlank { item.id.orEmpty() }
            }
            ExamRepository.loadTeacherPage(
                search = state.searchQuery,
                status = apiStatus,
                classSectionId = classSectionId,
                pageIndex = state.pageIndex,
                pageSize = state.pageSize,
                sortBy = state.sortField.toApiSortField(),
                order = if (state.sortAscending) "asc" else "desc",
                forceRemote = forceRemote,
                refreshScope = scope
            ).fold(
                onSuccess = { result ->
                    val lastPageIndex = (com.schoolconnect.app.feature.common.pageCountForTotal(result.totalCount, state.pageSize) - 1).coerceAtLeast(0)
                    if (state.pageIndex > lastPageIndex) {
                        _uiState.update { it.copy(pageIndex = lastPageIndex) }
                        loadPage(forceRemote = forceRemote)
                        return@fold
                    }
                    _uiState.update { it.copy(rows = result.response.items.orEmpty().map { exam ->
                        val examKey = exam.examId.orEmpty().ifBlank { exam.id.orEmpty() }
                        exam.toUi(resultProgressByExamId[examKey])
                    }, totalCount = result.totalCount, isLoading = false, errorMessage = "") }
                },
                onFailure = { throwable ->
                    scLog("[DATA_RETRIEVE_SC] ExamsViewModel loadPage failure", throwable)
                    _uiState.update { it.copy(rows = emptyList(), totalCount = 0, isLoading = false, errorMessage = throwable.message.orEmpty().ifBlank { "Unable to load exams." }) }
                }
            )
        }
    }

    private fun loadStatistics() {
        statisticsJob?.cancel()
        statisticsJob = scope.launch {
            _uiState.update { it.copy(isStatisticsLoading = true, statisticsErrorMessage = "") }
            ExamRepository.loadStatistics().fold(
                onSuccess = { response ->
                    val counters = response.counters
                    _uiState.update { it.copy(totalExams = counters?.total ?: 0, upcomingCount = counters?.upcoming ?: 0, ongoingCount = counters?.ongoing ?: 0, completedCount = counters?.completed ?: 0, isStatisticsLoading = false, statisticsErrorMessage = "") }
                },
                onFailure = { throwable ->
                    scLog("[DATA_RETRIEVE_SC] ExamsViewModel loadStatistics failure", throwable)
                    _uiState.update { it.copy(isStatisticsLoading = false, statisticsErrorMessage = throwable.message.orEmpty().ifBlank { "Unable to load exam statistics." }) }
                }
            )
        }
    }

    fun clear() { loadJob?.cancel(); statisticsJob?.cancel(); lookupJob?.cancel(); subjectLookupJob?.cancel(); saveJob?.cancel(); examDetailsJob?.cancel(); editResultsJob?.cancel(); insightsJob?.cancel(); importExportJob?.cancel(); scope.cancel() }

    private fun loadExamDetails(row: ExamRowUi) {
        examDetailsJob?.cancel()
        _uiState.update { it.copy(selectedExamDetails = row, isLoadingExamDetails = true, examDetailsError = "") }
        examDetailsJob = scope.launch {
            val examDeferred = async { ExamRepository.get(row.backendId) }
            val subjectOptionsDeferred = async { SubjectRepository.loadSchoolSubjectOptions(schoolId) }
            val examResult = examDeferred.await()
            val subjectOptionsResult = subjectOptionsDeferred.await()
            val loadedNamesById = subjectOptionsResult.getOrNull()?.nameById
            examResult.fold(
                onSuccess = { response ->
                    val subjectNames = loadedNamesById.orEmpty()
                    val existingNamesById = _uiState.value.subjectNamesById +
                        _uiState.value.subjectOptionIds.entries.associate { (name, id) -> id to name }
                    val availableNamesById = existingNamesById + subjectNames
                    val resolvedExam = response.toUi(row)
                    val unresolvedSubjectCount = resolvedExam.subjectConfigs.keys.count { subjectId ->
                        availableNamesById[subjectId].isNullOrBlank()
                    }
                    val subjectNameError = subjectOptionsResult.exceptionOrNull()?.let { throwable ->
                        "Unable to load subject names: ${throwable.message ?: "Please close and reopen exam details to retry."}"
                    } ?: unresolvedSubjectCount.takeIf { it > 0 }?.let { count ->
                        "Unable to resolve names for $count configured subject${if (count == 1) "" else "s"}. Check that the subjects are available in this school's subject list."
                    }.orEmpty()
                    _uiState.update { state ->
                        state.copy(
                            selectedExamDetails = resolvedExam,
                            subjectNamesById = loadedNamesById ?: state.subjectNamesById,
                            isLoadingExamDetails = false,
                            examDetailsError = subjectNameError
                        )
                    }
                },
                onFailure = { throwable ->
                    scLog("[DATA_RETRIEVE_SC] ExamsViewModel getExam failure examId=${row.backendId}", throwable)
                    _uiState.update { it.copy(isLoadingExamDetails = false, examDetailsError = throwable.message ?: "Unable to load exam details.") }
                }
            )
        }
    }

    private fun loadEditExam(row: ExamRowUi) {
        examDetailsJob?.cancel()
        _uiState.update { it.copy(selectedEditExam = row, isLoadingEditExam = true, editExamError = "") }
        examDetailsJob = scope.launch {
            ExamRepository.get(row.backendId).fold(
                onSuccess = { response ->
                    _uiState.update { state ->
                        state.copy(
                            selectedEditExam = response.toUi(row),
                            isLoadingEditExam = false,
                            editExamError = ""
                        )
                    }
                },
                onFailure = { throwable ->
                    scLog("[DATA_RETRIEVE_SC] ExamsViewModel getExam for edit failure examId=${row.backendId}", throwable)
                    _uiState.update { it.copy(isLoadingEditExam = false, editExamError = throwable.message ?: "Unable to load exam for editing.") }
                }
            )
        }
    }

    private fun parseDisplayDate(value: String): String? {
        val parts = value.trim().split(" ").filter { it.isNotBlank() }
        if (parts.size != 3) return null
        val month = mapOf("JAN" to "01", "FEB" to "02", "MAR" to "03", "APR" to "04", "MAY" to "05", "JUN" to "06", "JUL" to "07", "AUG" to "08", "SEP" to "09", "OCT" to "10", "NOV" to "11", "DEC" to "12")[parts[1].uppercase()] ?: return null
        val day = parts[0].toIntOrNull() ?: return null
        val year = parts[2].toIntOrNull() ?: return null
        if (day !in 1..31 || year !in 1900..2200) return null
        return "${year.toString().padStart(4, '0')}-$month-${day.toString().padStart(2, '0')}"
    }
}

private fun Double.toDisplayNumber(): String =
    if (this % 1.0 == 0.0) toInt().toString() else toString()

private fun com.schoolconnect.app.datasource.network.response.TeacherExamListItemResponse.toUi(
    resultProgress: com.schoolconnect.app.datasource.network.response.TeacherExamResultListItemResponse?
): ExamRowUi {
    return ExamRowUi(
        id = examId.orEmpty().ifBlank { id.orEmpty().ifBlank { "—" } },
        name = title.orEmpty().ifBlank { "Untitled exam" },
        type = SystemLookupOptions.label("EXAM_TYPE", examType.orEmpty()).ifBlank { examType.orEmpty().ifBlank { "—" } },
        classes = classes.orEmpty().map { listOfNotNull(it.className, it.sectionName).joinToString("-") }.filter(String::isNotBlank).ifEmpty { listOf("—") }.joinToString(", "),
        startDate = startDate.toDisplayDate(),
        status = status.orEmpty().ifBlank { "—" },
        endDate = endDate.toDisplayDate(),
        resultStatus = resultProgress?.marksEntered?.let { progress ->
            "${progress.entered ?: 0} / ${progress.total ?: 0}"
        } ?: "0 / 0",
        backendId = id.orEmpty().ifBlank { examId.orEmpty() },
        classSectionId = classSectionId.orEmpty(),
        hasOverallGrade = hasOverallGrade ?: true,
        hasOverallRank = hasOverallRank ?: true,
        hasOverallMarks = hasOverallMarks ?: true,
        maxMarks = maxMarks,
        subjectConfigs = subjectMarkConfigs.orEmpty().mapNotNull { config ->
            val subjectId = config["subject_id"]?.jsonPrimitive?.content?.trim().orEmpty()
            if (subjectId.isBlank()) null else subjectId to ExamSubjectSelectionUi(
                hasGrade = config["has_grade"]?.jsonPrimitive?.content?.toBoolean() == true,
                hasRank = config["has_rank"]?.jsonPrimitive?.content?.toBoolean() == true,
                hasMarks = config["has_marks"]?.jsonPrimitive?.content?.toBoolean() == true,
                internalMark = config["internal_mark"]?.jsonPrimitive?.content.orEmpty().removeSuffix(".0"),
                mainMark = config["main_mark"]?.jsonPrimitive?.content.orEmpty().removeSuffix(".0"),
                totalMark = config["total_mark"]?.jsonPrimitive?.content.orEmpty().removeSuffix(".0")
            )
        }.toMap()
    )
}

private fun com.schoolconnect.app.datasource.network.response.ExamResponse.toUi(existing: ExamRowUi): ExamRowUi {
    return ExamRowUi(
        id = id.orEmpty().ifBlank { existing.id },
        name = title.orEmpty().ifBlank { existing.name },
        type = SystemLookupOptions.label("EXAM_TYPE", examType.orEmpty()).ifBlank { examType.orEmpty().ifBlank { existing.type } },
        classes = existing.classes,
        startDate = startDate.toDisplayDate(),
        endDate = endDate.toDisplayDate(),
        status = status.orEmpty().ifBlank { existing.status },
        resultStatus = existing.resultStatus,
        backendId = id.orEmpty().ifBlank { existing.backendId },
        classSectionId = applicableClassSectionIds?.firstOrNull()?.trim().orEmpty().ifBlank { existing.classSectionId },
        hasOverallGrade = hasOverallGrade ?: false,
        hasOverallRank = hasOverallRank ?: false,
        hasOverallMarks = hasOverallMarks ?: false,
        maxMarks = maxMarks ?: existing.maxMarks,
        subjectConfigs = subjectMarkConfigs.orEmpty().mapNotNull { config ->
            val subjectId = config["subject_id"]?.jsonPrimitive?.content?.trim().orEmpty()
            if (subjectId.isBlank()) null else subjectId to ExamSubjectSelectionUi(
                hasGrade = config["has_grade"]?.jsonPrimitive?.content?.toBoolean() == true,
                hasRank = config["has_rank"]?.jsonPrimitive?.content?.toBoolean() == true,
                hasMarks = config["has_marks"]?.jsonPrimitive?.content?.toBoolean() == true,
                internalMark = config["internal_mark"]?.jsonPrimitive?.content.orEmpty().removeSuffix(".0"),
                mainMark = config["main_mark"]?.jsonPrimitive?.content.orEmpty().removeSuffix(".0"),
                totalMark = config["total_mark"]?.jsonPrimitive?.content.orEmpty().removeSuffix(".0")
            )
        }.toMap()
    )
}

private fun String?.toDisplayDate(): String {
    val parts = this.orEmpty().substringBefore('T').split('-')
    if (parts.size != 3) return this.orEmpty().ifBlank { "—" }
    val month = parts[1].toIntOrNull() ?: return this.orEmpty()
    val label = listOf("JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC").getOrNull(month - 1) ?: return this.orEmpty()
    return "${parts[2].padStart(2, '0')} $label ${parts[0]}"
}
